/**
 * @Description 踩点记录 列表
 */

import { FC, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useMethods } from '@lhb/hook';
import V2Table from '@/common/components/Data/V2Table';
import { V2Confirm } from '@/common/components/Others/V2Confirm';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { spotSurveyDelete, spotSurveyPage } from '@/common/api/expandStore/spotSurvey';
import V2Operate from '@/common/components/Others/V2Operate';
import { isArray, isNotEmpty, refactorPermissions } from '@lhb/func';
import dayjs from 'dayjs';
import { getSearchTypeConfigByStaticType } from '@/common/components/business/DynamicComponent/config';
import FormSearchUser from '@/common/components/Form/FormSearchUser';
import { Form } from 'antd';
import { useSelector } from 'react-redux';

/** 踩点记录权限 枚举 */
export enum SpotSurveyPermission {
  export = 'spotSurvey:export',
  delete = 'spotSurvey:delete',
}

const List: FC<{
  mainHeight;
  filters;
  onClickDetail;
  setLoading;
  tableProps?: any;
  setHasExportPermission?: any;
  setHasData?: any;
  ref?: any;
  setExportParams?: any;
  jumpParams?: any;
  isSjtb?: boolean;
}> = forwardRef(({
  mainHeight,
  filters,
  setLoading,
  onClickDetail,
  tableProps,
  setHasExportPermission,
  setHasData,
  setExportParams,
  jumpParams = {},
  isSjtb,
}, ref) => {
  const tableRef: any = useRef(null);
  const employeeSelectedOptionsRef = useRef<any[]>([]);
  const [appendColumns, setAppendColumns] = useState<any[]>([]);
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);
  const isDynamicHeaders = !!tenantCheckData?.isDynamicHeaders;

  useImperativeHandle(ref, () => ({
    resetHeadFilter: methods.resetHeadFilter,
  }));

  const methods = useMethods({
    getHeaderFilterConfig(config: any) {
      return isDynamicHeaders ? getSearchTypeConfigByStaticType(config) : undefined;
    },
    formatTableParams(params: any) {
      const time = params.time?.comType ? params.time.value : params.time;
      const hasEmployeeFilter = Object.prototype.hasOwnProperty.call(params, 'employeeIds');
      const hasTimeFilter = Object.prototype.hasOwnProperty.call(params, 'time');
      const requestParams = {
        ...jumpParams,
        ...params,
        code: params.code?.comType ? params.code.value : params.code,
        employeeIds: params.employeeIds?.comType ? params.employeeIds.value : params.employeeIds,
      };

      // 用户重新选择开发人员时，使用 employeeIds 覆盖 URL 下钻中的 employeeId。
      if (hasEmployeeFilter) {
        delete requestParams.employeeId;
      } else if (isNotEmpty(jumpParams.employeeId)) {
        requestParams.employeeIds = [jumpParams.employeeId];
        delete requestParams.employeeId;
      }

      // 开发部报表下钻时，将单个部门参数转换为踩点记录接口需要的部门数组。
      if (isNotEmpty(jumpParams.departmentId)) {
        requestParams.departmentIds = [jumpParams.departmentId];
        delete requestParams.departmentId;
      }

      // 用户重新选择或清空时间时，使用 time 转换结果覆盖 URL 下钻日期。
      if (hasTimeFilter) {
        delete requestParams.startDate;
        delete requestParams.endDate;
        requestParams.startDate = Array.isArray(time) && time.length > 0 ? dayjs(time[0]).format('YYYY-MM-DD') : undefined;
        requestParams.endDate = Array.isArray(time) && time.length > 1 ? dayjs(time[1]).format('YYYY-MM-DD') : undefined;
      }
      delete requestParams.time;
      return requestParams;
    },
    renderCode(value: any, record: any) {
      return <a onClick={() => onClickDetail(record)} style={{ color: '#2441B3' }}>{value}</a>;
    },
    renderOperate(value: any, record: any) {
      const permissions = value || [];
      return <V2Operate operateList={refactorPermissions(permissions.filter(item => item.event === SpotSurveyPermission.delete))} onClick={(btn) => methods[btn.func](record)} />;
    },
    renderSearchUser({
      targetValue = null,
      updateValue,
    }) {
      return <HeaderSearchUser
        name='employeeIds'
        placeholder={isSjtb ? '请输入维护人搜索' : '请输入创建人搜索'}
        targetValue={targetValue}
        updateValue={updateValue}
        cacheOptions={employeeSelectedOptionsRef.current}
        onSelectedOptionsChange={(options) => {
          employeeSelectedOptionsRef.current = Array.isArray(options) ? options : [];
        }}
      />;
    },
    refreshCurrentPage() {
      (tableRef.current as any).onload(true);
    },
    resetHeadFilter() {
      tableRef.current?.resetHeadFilter?.();
    },
    handleDelete(record: any) {
      V2Confirm({
        content: '确认删除吗？',
        onOk: () => {
          spotSurveyDelete({ id: record.id })
            .then(() => {
              V2Message.success('删除成功');
              methods.refreshCurrentPage();
            });
        },
      });
    },
    /**
     * @description 获取加载table表格数据。该函数依赖fitles变化自动触发
     * @param params fitles和搜索框参数（当前页面只有keyword）
     * @return table数据
     */
    async loadData(params: any) {
      const requestParams = methods.formatTableParams(params);
      setExportParams?.(requestParams);
      const data = await spotSurveyPage(requestParams).finally(() => {
        setLoading(false);
      });
      setLoading(false);

      setHasData(!!data.totalNum);

      // 检查导出权限
      const hasExportPermission =
        isArray(data?.meta?.permissions) &&
        !!data.meta.permissions.find((item: any) => item.event === SpotSurveyPermission.export);
      setHasExportPermission?.(hasExportPermission);

      if (data.meta.appendColumns?.length && !appendColumns.length) {
        setAppendColumns(data.meta.appendColumns);
      }
      return {
        dataSource: data.objectList || [],
        count: data.totalNum,
      };
    },
  });

  const columns = useMemo(() => {
    return [
      {
        title: isSjtb ? '机会点编号' : '踩点编号',
        dataIndex: 'code',
        key: 'code',
        width: 120,
        render: methods.renderCode,
        filterConfig: methods.getHeaderFilterConfig({ customKey: 'code', type: 'formInput' }),
      },
      appendColumns.includes('numberOfVisits') ? {
        title: '走访次数',
        dataIndex: 'numberOfVisits',
        key: 'numberOfVisits',
        width: 100,
      } : undefined,
      appendColumns.includes('purposeOfVisit') ? {
        title: '走访目的',
        dataIndex: 'purposeOfVisit',
        key: 'purposeOfVisit',
        width: 150,
      } : undefined,
      {
        title: isSjtb ? '机会点位置' : '踩点位置',
        dataIndex: 'address',
        key: 'address',
        width: 100,
      },
      {
        title: isSjtb ? '维护人' : '创建人',
        dataIndex: 'employeeName',
        key: 'employeeName',
        width: 80,
        filterConfig: methods.getHeaderFilterConfig({ customKey: 'employeeIds', type: 'custom', render: methods.renderSearchUser }),
      },
      {
        title: '创建时间',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 80,
        filterConfig: methods.getHeaderFilterConfig({ customKey: 'time', type: 'formRangePicker' }),
      },
      {
        key: 'permissions',
        title: '操作',
        width: 150,
        fixed: 'right',
        render: methods.renderOperate,
      },
    ].filter(Boolean);
  }, [appendColumns, isDynamicHeaders, isSjtb]);

  return (
    <>
      <V2Table
        // 如果appendColumns有值就重载table
        key={appendColumns?.length}
        ref={tableRef}
        defaultColumns={columns}
        onFetch={methods.loadData}
        hideColumnPlaceholder
        voluntarilyEmpty
        filters={filters}
        rowKey='id'
        // 64是分页模块的总大小， 42是table头部
        scroll={{ y: mainHeight - 64 - 42, x: 'max-content' }}
        {...tableProps}
      />
    </>
  );
});

export default List;

const HeaderSearchUser: FC<{
  name: string;
  placeholder: string;
  targetValue,
  updateValue,
  cacheOptions?: any[];
  onSelectedOptionsChange?: (options: any[]) => void;
}> = ({
  name,
  placeholder,
  targetValue,
  updateValue,
  cacheOptions = [],
  onSelectedOptionsChange
}) => {
  const [form] = Form.useForm();
  const searchUserRef: any = useRef(null);

  useEffect(() => {
    form.setFieldsValue({
      [name]: Array.isArray(targetValue) ? targetValue : [],
    });
  }, [form, name, targetValue]);

  useEffect(() => {
    if (!Array.isArray(targetValue) || !targetValue.length) {
      onSelectedOptionsChange?.([]);
      return;
    }
    if (!Array.isArray(cacheOptions) || !cacheOptions.length) return;
    searchUserRef.current?.setOptions?.(cacheOptions, true);
  }, [cacheOptions, onSelectedOptionsChange, targetValue]);

  const methods = useMethods({
    handleChange(value) {
      updateValue(value);
      const selectedOptions = searchUserRef.current?.getItem?.(value);
      const nextOptions = Array.isArray(selectedOptions) ? selectedOptions : (selectedOptions ? [selectedOptions] : []);
      onSelectedOptionsChange?.(nextOptions);
    },
  });

  return <Form form={form}>
    <FormSearchUser
      ref={searchUserRef}
      name={name}
      label=''
      placeholder={placeholder}
      mode='multiple'
      maxTagCount='responsive'
      needCacheSelect
      onChange={methods.handleChange}
    />
  </Form>;
};
